import React from 'react';
import ImageSlider from './ImageSlider';
import { useBeforeAfterSlider } from '../hooks/useBeforeAfterSlider';
import { getBestImageUrl } from '../utils/imageUrl';

interface StrapiImageFormat {
  url: string;
  width: number;
  height: number;
  size: number;
  ext: string;
  mime: string;
  hash: string;
  name: string;
  path: string | null;
  sizeInBytes: number;
}

interface BeforeAfterSliderItem {
  id: number;
  documentId: string;
  altText: string;
  order: number;
  image?: {
    id: number;
    url: string;
    alternativeText?: string;
    formats?: {
      large?: StrapiImageFormat;
      medium?: StrapiImageFormat;
      small?: StrapiImageFormat;
      thumbnail?: StrapiImageFormat;
    };
    width?: number;
    height?: number;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

const BeforeAfterSlider: React.FC = () => {
  const { data, loading, error } = useBeforeAfterSlider();

  // Cast data to proper type for debugging
  const sliderData = data as BeforeAfterSliderItem[] | null;
  
  // Debug logging for before-after slider data
  if (sliderData && !loading && !error) {
    console.log('🖼️ BeforeAfterSlider - ✅ Strapi data loaded:', !!sliderData);
    console.log('🖼️ BeforeAfterSlider - Items count:', sliderData.length);
    console.log('🖼️ BeforeAfterSlider - First item structure:', sliderData[0]);
  }

  if (loading) {
    return (
      <div className="image-slider-section">
        <div className="image-slider-container">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
          </div>
        </div>
      </div>
    );
  }

  // Data is already cast above
  
  if (error || !sliderData || sliderData.length === 0) {
    // Show loading state if no data available
    return (
      <div className="image-slider-section">
        <div className="image-slider-container">
          <div className="loading-placeholder">
            <div className="loading-title">No before/after images available</div>
          </div>
        </div>
      </div>
    );
  }

  // Transform Strapi data to slider format - only include items with valid images
  const sliderImages = sliderData
    .filter(item => item.image && item.image.url)
    .map((item) => {
      const imageUrl = getBestImageUrl(item.image!);
      return {
        src: imageUrl || '', // Ensure src is never null
        alt: item.altText || item.image!.alternativeText || "Before and after comparison"
      };
    })
    .filter(item => item.src); // Remove items with empty src

  return (
    <ImageSlider 
      images={sliderImages}
      className="before-after-slider"
    />
  );
};

export default BeforeAfterSlider; 